import { Spin, Modal, Button } from 'antd';
import { ModalProps as AntModalProps } from 'antd/lib/modal';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineExclamationCircle } from 'react-icons/ai';

type modalType =
  | 'normal'
  | 'create'
  | 'update'
  | 'delete'
  | 'confirm'
  | 'taoLaiDon'
  | 'thuNo'
  | 'cancel'
  | 'decline'
  | 'primary'
  | 'dashed'
  | 'link'
  | 'text'
  | 'excel'
  | 'reset';

type colorType = 'primary' | 'danger' | 'normal' | 'dashed' | 'link' | 'text';

type modalSize = 'fullscreen' | 'large' | 'medium' | 'normal' | 'auto';

type modalFooterButton = {
  style?: colorType;
  text: string;
  key: string;
  danger?: boolean;
  ghost?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  onClick: () => void;
};
export interface ModalProps extends AntModalProps {
  loadingData?: boolean;
  children?: React.ReactNode;
  justifyMiddle?: boolean;
  hiddenFooter?: boolean;
  okStyle?: colorType;
  modalType?: modalType;
  modalTitle?: string | React.ReactNode;
  modalSize?: modalSize;
  footerButtons?: Array<modalFooterButton>;
  clickOutSide?: boolean;
}

export default function ModalComponent({
  loadingData = false,
  children,
  modalType = 'normal',
  modalSize = 'auto',
  footerButtons = [],
  ...props
}: ModalProps) {
  const { t } = useTranslation();

  const handleGetButtonStyle: any = (val?: string) => {
    switch (val) {
      case 'normal':
        return 'default';

      case 'excel':
        return 'primary';

      case 'create':
        return 'primary';

      case 'update':
        return 'primary';

      case 'delete':
        return 'primary';

      case 'cancel':
        return 'primary';

      case 'decline':
        return 'primary';

      case 'reset':
        return 'primary';

      case 'confirm':
        return 'primary';

      case 'taoLaiDon':
        return 'primary';

      case 'dashed':
        return 'dashed';

      case 'link':
        return 'link';

      case 'text':
        return 'text';

      case 'primary':
        return 'primary';

      default:
        return 'default';
    }
  };

  const okType = React.useMemo(() => {
    return handleGetButtonStyle(modalType);
  }, [modalType]);

  const widthScreen = window.innerWidth;

  const modalTitle = React.useMemo(() => {
    switch (modalType) {
      case 'delete':
        return (
          <div className="flex text-base flex-row space-x-8 items-center">
            <AiOutlineExclamationCircle color={'#f44'} size={16} />
            <label>{props.modalTitle}</label>
          </div>
        );
      default:
        return <label>{props.modalTitle}</label>;
    }
  }, [props.modalTitle, modalType]);

  const okTextModal = React.useMemo(() => {
    switch (modalType) {
      case 'create':
        return t('Table.Tooltip.Create');

      case 'excel':
        return t('Table.Tooltip.Excel');

      case 'update':
        return t('Table.Tooltip.Update');

      case 'delete':
        return t('Table.Tooltip.Delete');

      case 'confirm':
        return t('Table.Tooltip.Accept');

      case 'taoLaiDon':
        return t('Table.Tooltip.CreateOrderAgain');

      case 'cancel':
        return t('Table.Tooltip.Cancel');

      case 'decline':
        return t('Table.Tooltip.Decline');

      case 'reset':
        return t('Table.Tooltip.Reset');
      default:
        return 'default';
    }
  }, []);

  const modalSizeValue = React.useMemo(() => {
    switch (modalSize) {
      case 'auto':
        return undefined;

      case 'fullscreen':
        return widthScreen - 25;

      case 'large':
        return widthScreen - 200;

      case 'medium':
        return 1000;

      case 'normal':
        return 600;
      default:
        return undefined;
    }
  }, [modalSize, widthScreen]);

  const footerComponent = React.useMemo(() => {
    if (footerButtons.length === 0) {
      return undefined;
    }
    let footerButtonsList: any[] = [];
    footerButtonsList.push(
      <Button key="back" onClick={props.onCancel}>
        {t('Button.Back')}
      </Button>
    );
    footerButtonsList = footerButtonsList.concat(
      footerButtons.map(item => {
        return (
          <Button
            style={{
              background: item.text == 'Đổi loại đơn' ? '#36cfc9' : ''
            }}
            key={item.key}
            danger={item.danger}
            ghost={item.ghost}
            type={handleGetButtonStyle(item.style)}
            onClick={item.onClick}
            disabled={item.disabled}
            hidden={item.hidden}
          >
            {item.text}
          </Button>
        );
      })
    );
    footerButtonsList.push(
      <Button key="submit" type={okType} onClick={props.onOk}>
        {okTextModal}
      </Button>
    );
    return footerButtonsList;
  }, [footerButtons, okTextModal, okType]);

  return (
    <Modal
      title={modalTitle}
      okText={okTextModal}
      okType={okType}
      cancelText={t('Button.Back')}
      maskClosable={props.clickOutSide || false}
      keyboard={true}
      width={modalSizeValue}
      centered={true}
      footer={props.hiddenFooter ? null : footerComponent}
      onCancel={props.onCancel}
      {...props}
      style={{ padding: '0 important', overflowX: 'hidden', height: props.justifyMiddle ? 'unset' : '100vh' }}
    >
      <Spin spinning={loadingData}>{children}</Spin>
    </Modal>
  );
}

ModalComponent.confirm = Modal.confirm;
ModalComponent.error = Modal.error;
ModalComponent.success = Modal.success;
ModalComponent.info = Modal.info;
ModalComponent.displayName = 'Modal';
